<script setup lang="ts">
import { ref, computed } from 'vue'
import type { Study } from '@pocketprep/types'
import UIKit from '@pocketprep/ui-kit'
import { analyticsModule } from '@/store/analytics/module'

const {
    Button: PocketButton,
    Question,
    Icon,
} = UIKit

const answer = ref<Study.Cloud.IQuizAnswer | null>(null)
const continueButtonClicked = ref(false)

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const question =
    {
        'choices': [
            {
                'text': '<p>Increased intracranial pressure (ICP)</p>',
                'isCorrect': true,
                'id': 'a1',
            },
            {
                'text': '<p>Hyperglycemia</p>',
                'isCorrect': false,
                'id': 'd1',
            },
            {
                'text': '<p>Hypovolemic shock</p>',
                'isCorrect': false,
                'id': 'd2',
            },
            {
                'text': '<p>Heatstroke</p>',
                'isCorrect': false,
                'id': 'd3',
            },
        ],
        'references': [
            // eslint-disable-next-line max-len
            '<p><i>Emergency Care and Transportation of the Sick and Injured Essentials Package, 12th Edition (ISBN 978-1284246223)</i>. Pg 749-750.</p>',
        ],
        // eslint-disable-next-line max-len
        'prompt': '<p>You are reassessing a patient with a suspected head injury. The patient was initially alert but now responds only to painful stimuli and has unequal pupils. What condition should you suspect?</p>',
        // eslint-disable-next-line max-len
        'explanation': '<p>Correct answer: Increased intracranial pressure (ICP)</p><p>A declining level of consciousness combined with unequal pupils is strongly suggestive of increased intracranial pressure (ICP) due to a traumatic brain injury. As pressure builds within the skull, it can compress cranial nerves—especially the oculomotor nerve—leading to pupil dilation on one side, as well as deteriorating neurological status. Other signs may include vomiting, bradycardia, and hypertension.</p><p>Hyperglycemia can cause confusion or unresponsiveness, but typically does not affect pupil symmetry.</p><p>Hypovolemic shock results in hypotension and tachycardia but not focal neurologic signs like unequal pupils.</p><p>Heatstroke can alter mental status and body temperature, but does not cause asymmetric pupils.</p>',
        'serial': 'qQvhNIfYRe',
        'compositeKey': 'ppef01/2.5.5',
        'type': 'Multiple Choice',
        'bloomTaxonomyLevel': 'Analyze',
    } as unknown as Study.Class.QuestionJSON

const answerSelected = () => {
    analyticsModule.actions.amplitudeTrack('Try a Question Answer Selected')
}

const checkAnswer = (answerParam: Study.Cloud.IQuizAnswer) => {
    answer.value = answerParam
    analyticsModule.actions.amplitudeTrack('Try a Question Answer Submitted', { ...answerParam })
}

const clickContinueButton = () => {
    analyticsModule.actions.amplitudeTrack('Try a Question Continue Clicked')
    continueButtonClicked.value = true
}

const navigateToRegister = () => {
    analyticsModule.actions.amplitudeTrack('Try a Question Call to Action Clicked')
    const ampSessionId = analyticsModule.actions.getAmplitudeSessionId()
    const ampDeviceId = analyticsModule.actions.getAmplitudeDeviceId()
    const registerUrl = new URL('https://study.pocketprep.com/register')
    if (ampSessionId) {
        registerUrl.searchParams.set('sessionId', String(ampSessionId))
    }
    if (ampDeviceId) {
        registerUrl.searchParams.set('deviceId', ampDeviceId)
    }
    window.open(registerUrl.toString(), '_blank')
}
</script>

<template>
    <Question
        v-if="!continueButtonClicked"
        class="try-a-question"
        :question="question"
        :quiz-mode="'quick10'"
        :show-next-question="false"
        :show-check-answer="true"
        :initial-show-answers="!!answer"
        :allow-keyboard-shortcuts="false"
        :image-url-prefix="`${s3Url || ''}/${question.compositeKey.toUpperCase()}/`"
        :hide-references="false"
        :keyword-definitions="[]"
        :show-close-button="false"
        @checkAnswer="checkAnswer"
        @selectedChoices="answerSelected"
    >
        <template #context>
            <p>Try a question?</p>
        </template>
        <template #actionExperiment v-if="!!answer">
            <PocketButton
                type="primary"
                @click="clickContinueButton"
            >
                Continue <Icon type="arrow" />
            </PocketButton>
        </template>
    </Question>
    <div class="try-a-question try-a-question__complete" v-else>
        <h2 class="try-a-question__complete-call-to-action-title">Nice Work!</h2>
        <p class="try-a-question__complete-call-to-action-label">
            Now - create a <strong>free</strong> account to receive 65 additional questions at no cost!
        </p>
        <PocketButton
            type="primary"
            @click="navigateToRegister"
        >
            Create <strong>Free</strong> Account
        </PocketButton>
    </div>
</template>

<style lang="scss" scoped>
.try-a-question {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: scroll;
    background-color: $gray-background;

    :deep(.uikit-question__close-icon) {
        display: none;
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__complete {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
    }

    &__complete-call-to-action-title {
        font-weight: 700;
        font-size: 42px;
        line-height: 100%;
    }

    &__complete-call-to-action-label {
        font-weight: 400;
        font-size: 24px;
        max-width: 492px;
    }
}
</style>
